import React from 'react'
import { Formik } from 'formik'
import { useCallback, useState } from 'react'
import { useDispatch, useSelector } from 'react-redux'
import { CreatePartnerDriver } from 'src/constants/actionTypes'
import { createAsyncAction } from 'src/utils/reduxUtils'
import { InviteWorkersViaMessageFormValues } from './InviteWorkersViaMessageForm.interface'
import { InviteWorkersViaMessageSchema } from './InviteWorkersViaMessageForm.validator'
import { getMarkets, getSelectedMarket } from 'src/selectors/market'
import { showToast } from 'src/utils/toast'
import { getPartnerId } from 'src/selectors/user'
import { getOnboardingTemplates } from 'src/selectors/onboarding'
import { Tag } from 'src/interfaces/tag'
import { handleApiError } from 'src/utils/errorHandlers'
import { BasicModalDescription, BasicModalHeader } from 'src/components/shared/modal'
import { InviteWorkersViaMessageFormContent } from './InviteWorkersViaMessageFormContent'
import { ChatMajor } from '@shopify/polaris-icons'
import tw from 'twin.macro'
import { Divider } from '@mui/material'

const initialValues: InviteWorkersViaMessageFormValues = {
  firstName: '',
  lastName: '',
  phone: '',
  email: '',
}

interface Props {
  onSubmit(): void
  onClose(): void
}

export const InviteWorkersViaMessageForm = ({ onSubmit, onClose }: Props) => {
  const dispatch = useDispatch()

  const selectedMarket = useSelector(getSelectedMarket)
  const markets = useSelector(getMarkets)
  const onboardingTemplates = useSelector(getOnboardingTemplates)
  const partnerId = useSelector(getPartnerId)

  const [isLoading, setIsLoading] = useState<boolean>(false)
  const [selectedTags, setSelectedTags] = useState<Array<Tag>>([])

  const handleFormSubmit = async ({
    collectDataTemplateId,
    ...values
  }: InviteWorkersViaMessageFormValues) => {
    if (!partnerId) {
      return
    }

    setIsLoading(true)

    try {
      await createAsyncAction(
        dispatch,
        CreatePartnerDriver.request({
          partnerId,
          collectDataTemplateId,
          tagNames: selectedTags.map((item) => item.name),
          ...values,
        }),
      )

      showToast('Invite sent!', { variant: 'success' })

      onSubmit()
      onClose()
    } catch (err: any) {
      handleApiError(err)
    } finally {
      setIsLoading(false)
    }
  }

  const handleSelectTag = useCallback((newTag: Tag) => {
    setSelectedTags((prev) => [...prev, newTag])
  }, [])

  const handleRemoveTag = useCallback((removedTag: Tag) => {
    setSelectedTags((prev) => [...prev.filter((item) => item.name !== removedTag.name)])
  }, [])

  return (
    <div css={tw`max-w-[528px]`} data-testid="contractors-onboarding-invite-message-modal">
      <BasicModalHeader
        title="Invite contractor"
        onClose={onClose}
        closeButtonDataTestId="contractors-onboarding-invite-message-modal-close-btn"
      />
      <BasicModalDescription
        title="Invite via text message"
        icon={<ChatMajor width={20} height={20} />}
        description="Enter the contractor’s basic info and select preferences — the invite will be sent when you click ‘Send Invite’"
      />
      <Divider css={tw`mt-4 mx-4 border-[#EDEDED] `} />
      <Formik
        initialValues={{
          ...initialValues,
          partnerMarketId: selectedMarket?.id,
          collectDataTemplateId:
            onboardingTemplates?.length === 1 ? onboardingTemplates[0].id : undefined,
        }}
        onSubmit={handleFormSubmit}
        validationSchema={InviteWorkersViaMessageSchema}
      >
        <InviteWorkersViaMessageFormContent
          isLoading={isLoading}
          selectedTags={selectedTags}
          handleSelectTag={handleSelectTag}
          handleRemoveTag={handleRemoveTag}
          onboardingTemplates={onboardingTemplates}
          markets={markets}
        />
      </Formik>
    </div>
  )
}
